import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { useActionLogger } from '../hooks/useActionLogger';
import { useTranslation } from '../hooks/useTranslation';
import FloatingToolbar from './FloatingToolbar';
import ZoomControls from './ZoomControls';
import ControlsOverlayCanvas from './ControlsOverlayCanvas';
// 🎨 Não precisa mais do SimplePaintLayer - usando modal agora
import useCanvasScaling from '../hooks/useCanvasScaling';

const FluidCanvas = () => {
  const { t } = useTranslation();
  const {
    uploadedImages,
    addImage,
    updateImageTransform, setSelectedImage,
    selectedImageId, saveHistoryState,
    mobileLayerDrawerOpen,
    undo,
    redo,
    canUndo,
    canRedo,
    // 🎨 Efeitos
    isEffectsPanelOpen,
    effectsEditingImage,
    activeEffectCanvas,
    effectUpdateCounter,
    activeEffectType,
    effectSettings,
    updateEffectSettings,
    effectActiveTab,
    zoomBlurActiveTab,
    // 🎨 Filtros de Cor (NOVO SISTEMA)
    isColorFiltersPanelOpen,
    colorFilterEditingImage,
    activeColorFilterCanvas,
    colorFilterUpdateCounter,
    activeColorFilterType,
    colorFilterSettings,
    // 🎨 Filtros de Cor (LEGACY)
    selectedColorFilter,
    colorBlendMode,
    colorOpacity,
    // 🎨 Pintura
    paintingSettings,
    isSimplePaintingMode, // ✨ Modo pintura simplificado (quando o canvas de pintura está ativo)
    isPainting2Mode, // 🎨 Modo Pintura2
  } = useApp();

  // 📋 Hook de logging
  const { logAction } = useActionLogger();

  // Usar o hook de scaling para HiDPI/Retina
  const {
    canvasRef,
    context: canvasContext,
    devicePixelRatio,
    resizeCanvas,
    getMousePosition,
    clearCanvas,
    drawRect,
    drawCircle,
    drawLine,
    drawText
  } = useCanvasScaling({ autoResize: true });

  // Refs para edição de imagem
  const isImageEditingRef = useRef(false);
  const imageEditStartRef = useRef(null);
  const imageEditTypeRef = useRef(null); // 'move', 'resize', 'rotate'
  const imageEditHandleRef = useRef(null); // qual handle está sendo usado
  const editingImageIdRef = useRef(null); // qual imagem está sendo editada
  const lastMousePosRef = useRef(null); // última posição do mouse para movimento suave
  const animationFrameRef = useRef(null); // para cancelar animações
  const lastUpdateTimeRef = useRef(0); // para debounce de atualizações
  const drawImagesRef = useRef(null); // referência para função drawImages
  const [canvasScale, setCanvasScale] = useState(1);
  const [paperPosition, setPaperPosition] = useState({
    x: 0,
    y: 0,
    scale: 1,
    width: 0,
    height: 0
  });

  // Estado para a toolbar flutuante
  const [toolbarVisible, setToolbarVisible] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ x: 0, y: 0 });
  const [toolbarDirection, setToolbarDirection] = useState('below');
  
  // Estados para controle de ponto interativo
  const [isDraggingPoint, setIsDraggingPoint] = useState(false);
  const isDraggingPointRef = useRef(false);
  
  // 🎯 Estados para controle dos círculos de transição do Zoom Blur
  const [draggingTransitionControl, setDraggingTransitionControl] = useState(null); // 'center', 'outer', 'inner'
  const [draggingTransitionAngle, setDraggingTransitionAngle] = useState(null); // Ângulo do ponto sendo arrastado
  const draggingTransitionRef = useRef({ type: null, angle: null });
  
  // 🎯 Estados de hover para os controles de transição
  const [hoveredTransitionControl, setHoveredTransitionControl] = useState(null); // { type, angle }

  // 🆕 Estado para hover nos handles
  const [hoveredHandle, setHoveredHandle] = useState(null);
  const [hoveredImageId, setHoveredImageId] = useState(null);

  // 🔍 Estado para zoom do canvas
  const [zoom, setZoom] = useState(1.0);
  const [animatedZoom, setAnimatedZoom] = useState(1.0);
  const zoomAnimationRef = useRef(null);

  // 📱 Estados para pinch-to-zoom no mobile
  const touchStartDistanceRef = useRef(null);
  const touchStartZoomRef = useRef(null);
  const lastTouchesRef = useRef([]);
  const isPinchingRef = useRef(false); // Flag para indicar que está fazendo pinch
  
  // 📱 Estados para pan (arrastar) no mobile
  const touchPanStartRef = useRef(null);
  const paperPositionStartRef = useRef(null);

  // 📤 Ref para input de upload
  const fileInputRef = useRef(null);
  const [recentUploads, setRecentUploads] = useState(() => {
    try {
      const raw = localStorage.getItem('recentUploads:v1');
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  });

  const saveRecentUploads = useCallback((list) => {
    try {
      localStorage.setItem('recentUploads:v1', JSON.stringify(list));
    } catch {}
  }, []);

  const addToRecent = useCallback((entry) => {
    setRecentUploads(prev => {
      const withoutDup = prev.filter(x => x.dataUrl !== entry.dataUrl);
      const next = [entry, ...withoutDup].slice(0, 12);
      saveRecentUploads(next);
      return next;
    });
  }, [saveRecentUploads]);

  const loadFromRecent = useCallback((item) => {
    const img = new Image();
    img.onload = () => {
      const newImage = {
        id: `image-${Date.now()}-${Math.random()}`,
        image: img,
        src: item.dataUrl,
        originalSrc: item.dataUrl,
        originalFileName: item.name || 'recent-upload.png',
        transform: { x: 0, y: 0, width: item.width, height: item.height, scale: 1, rotation: 0 },
        opacity: 100,
        blendMode: 'normal',
        isVisible: true,
        width: item.width,
        height: item.height
      };
      addImage(newImage);
    };
    img.src = item.dataUrl;
  }, [addImage, uploadedImages.length]);
  
  // 📤 Handler para upload de imagens
  const handleImageUpload = useCallback(async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    for (const file of files) {
      // 🆕 UPLOAD PARA BACKEND (Resolução Original)
      let backendImageId = null;
      let backendUploadSuccess = false;
      
      try {
        const backendService = (await import('../services/backendService')).default;
        
        // Verifica se backend está disponível
        const health = await backendService.checkHealth();
        
        if (health.available) {
          console.log('📤 Fazendo upload para backend (resolução original)...');
          const uploadResult = await backendService.uploadImage(file);
          backendImageId = uploadResult.image_id;
          backendUploadSuccess = true;
          console.log(`✅ Upload para backend concluído! ID: ${backendImageId}`);
          console.log(`📊 Tamanho original: ${uploadResult.size.width}x${uploadResult.size.height}`);
        } else {
          console.warn('⚠️ Backend offline - imagem não será enviada para processamento');
        }
      } catch (error) {
        console.error('❌ Erro ao fazer upload para backend:', error);
        console.warn('⚠️ Continuando sem backend - processamento local apenas');
      }

      // CONTINUA COM PROCESSAMENTO LOCAL (para exibição)
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Redimensionar imagem para no máximo 600px de largura
          const maxWidth = 600;
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }

          // Criar canvas temporário para redimensionar
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Converter canvas para data URL
          const resizedSrc = canvas.toDataURL('image/png');
          // Salvar nos uploads recentes (localStorage)
          addToRecent({
            name: file.name,
            dataUrl: resizedSrc,
            width,
            height,
            ts: Date.now()
          });

          // Criar nova imagem HTMLImageElement com a imagem redimensionada
          const resizedImg = new Image();
          resizedImg.onload = () => {

            // Criar nova imagem preenchendo todo o espaço da folha
            const newImage = {
              id: `image-${Date.now()}-${Math.random()}`,
              image: resizedImg,
              src: resizedSrc,
              originalSrc: resizedSrc,
              // 🆕 SALVAR ID DO BACKEND
              backendImageId: backendImageId,
              backendUploadSuccess: backendUploadSuccess,
              originalFileName: file.name,
              transform: {
                x: 0, // Posição inicial (0,0 para preencher toda folha)
                y: 0,
                width: width,
                height: height,
                scale: 1, // ✅ Escala inicial 1:1
                rotation: 0
              },
              opacity: 100,
              blendMode: 'normal',
              isVisible: true,
              width: width,
              height: height
            };

            addImage(newImage);
            
            // 📋 LOG: Upload de imagem
            logAction('UPLOAD_IMAGE', newImage.id, {
              filename: file.name,
              width: width,
              height: height,
              position: { x: 0, y: 0 },
              backendId: backendImageId,
              backendSuccess: backendUploadSuccess
            });
            
            // 🆕 Feedback para usuário
            if (backendUploadSuccess) {
              console.log(`🎉 Imagem "${file.name}" pronta:
  📱 Exibição no canvas: ${width}x${height}px
  ☁️ Processamento no backend: Resolução original
  🔑 Backend ID: ${backendImageId}`);
            } else {
              console.log(`📱 Imagem "${file.name}" carregada (${width}x${height}px) - Apenas local`);
            }
          };
          resizedImg.src = resizedSrc;
        };
        img.src = e.target.result;
      };

      reader.readAsDataURL(file);
    }
    
    // Limpar input
    event.target.value = '';
  }, [addImage, uploadedImages]);
  
  // Função para calcular posição da toolbar
  const calculateToolbarPosition = useCallback((imageData) => {
    if (!imageData) return { x: 0, y: 0, direction: 'below' };
    
    const { transform } = imageData;
    
    // Calcular posição central da imagem no canvas
    const imageCenterX = paperPosition.x + (transform.x * paperPosition.scale) + (transform.width * paperPosition.scale) / 2;
    const imageCenterY = paperPosition.y + (transform.y * paperPosition.scale) + (transform.height * paperPosition.scale) / 2;
    
    // Obter posição do canvas na tela
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const canvasRect = canvas.getBoundingClientRect();
    
    // Converter para coordenadas da tela
    const screenX = canvasRect.left + imageCenterX;
    const screenY = canvasRect.top + imageCenterY;
    
    // Calcular limites da folha na tela
    const paperTop = canvasRect.top + paperPosition.y;
    const paperBottom = canvasRect.top + paperPosition.y + paperPosition.height;
    const paperLeft = canvasRect.left + paperPosition.x;
    const paperRight = canvasRect.left + paperPosition.x + paperPosition.width;
    
    // Calcular posição da imagem na tela
    const imageTop = canvasRect.top + paperPosition.y + (transform.y * paperPosition.scale);
    const imageBottom = canvasRect.top + paperPosition.y + (transform.y * paperPosition.scale) + (transform.height * paperPosition.scale);
    const imageLeft = canvasRect.left + paperPosition.x + (transform.x * paperPosition.scale);
    const imageRight = canvasRect.left + paperPosition.x + (transform.x * paperPosition.scale) + (transform.width * paperPosition.scale);
    
    // Verificar se há espaço abaixo da imagem
    const spaceBelow = paperBottom - imageBottom;
    const toolbarHeight = 80; // Altura aproximada da toolbar
    
    let finalY = screenY;
    
    if (spaceBelow >= toolbarHeight + 20) {
      // Há espaço suficiente abaixo da imagem
      finalY = imageBottom + 20;
      return { x: screenX, y: finalY, direction: 'below' };
    } else {
      // Não há espaço abaixo, verificar se há espaço ao lado da imagem
      const spaceRight = paperRight - imageRight;
      const spaceLeft = imageLeft - paperLeft;
      const toolbarWidth = 180; // Largura aproximada da toolbar (3 ícones)
      
      if (spaceRight >= toolbarWidth + 20) {
        // Posicionar à direita da imagem
        finalY = imageCenterY;
        return { x: imageRight + 20, y: finalY, direction: 'right' };
      } else if (spaceLeft >= toolbarWidth + 20) {
        // Posicionar à esquerda da imagem
        finalY = imageCenterY;
        return { x: imageLeft - 20, y: finalY, direction: 'left' };
      } else {
        // Não há espaço ao lado da imagem, verificar se há espaço ao lado da folha
        const canvasRight = canvasRect.right;
        const canvasLeft = canvasRect.left;
        const spaceRightOfPaper = canvasRight - paperRight;
        const spaceLeftOfPaper = paperLeft - canvasLeft;
        
        if (spaceRightOfPaper >= toolbarWidth + 20) {
          // Posicionar à direita da folha
          finalY = paperTop + (paperPosition.height / 2);
          return { x: paperRight + 20, y: finalY, direction: 'right' };
        } else if (spaceLeftOfPaper >= toolbarWidth + 20) {
          // Posicionar à esquerda da folha
          finalY = paperTop + (paperPosition.height / 2);
          return { x: paperLeft - 20, y: finalY, direction: 'left' };
        } else {
          // Última opção: posicionar acima da imagem
          finalY = imageTop - 20;
          return { x: screenX, y: finalY, direction: 'above' };
        }
      }
    }
  }, [paperPosition]);
  
  // Função para lidar com cliques na toolbar
  const handleToolClick = useCallback((toolId) => {
    if (!selectedImageId) return;
    
    const selectedImage = uploadedImages.find(img => img.id === selectedImageId);
    if (!selectedImage) return;
    
    const { transform } = selectedImage;
    // Usar a primeira imagem como referência para dimensões do papel
    const firstImage = uploadedImages[0];
    if (!firstImage || !firstImage.image) {
      console.error('Nenhuma imagem disponível para referência');
      return;
    }
    const paperWidth = firstImage.image.width;
    const paperHeight = firstImage.image.height;
    
    let newTransform = { ...transform };
    
    switch (toolId) {
      case 'fillHeight':
        // Preencher altura total (centralizada horizontalmente)
        const aspectRatio = transform.width / transform.height;
        newTransform.height = paperHeight;
        newTransform.width = paperHeight * aspectRatio;
        newTransform.x = (paperWidth - newTransform.width) / 2; // Centralizar horizontalmente
        newTransform.y = 0; // Topo da folha
        break;
        
      case 'fillWidth':
        // Preencher largura total (centralizada verticalmente)
        const aspectRatio2 = transform.width / transform.height;
        newTransform.width = paperWidth;
        newTransform.height = paperWidth / aspectRatio2;
        newTransform.x = 0; // Lado esquerdo da folha
        newTransform.y = (paperHeight - newTransform.height) / 2; // Centralizar verticalmente
        break;
        
      case 'fillAll':
        // Preencher todo o espaço (aumentar proporcionalmente até cobrir toda a folha)
        const aspectRatio3 = transform.width / transform.height;
        const paperAspectRatio = paperWidth / paperHeight;
        
        if (aspectRatio3 > paperAspectRatio) {
          // Imagem é mais larga que a folha, preencher altura (corta largura)
          newTransform.height = paperHeight;
          newTransform.width = paperHeight * aspectRatio3;
          // Centralizar horizontalmente (corta partes esquerda/direita)
          newTransform.x = (paperWidth - newTransform.width) / 2;
          newTransform.y = 0;
        } else {
          // Imagem é mais alta que a folha, preencher largura (corta altura)
          newTransform.width = paperWidth;
          newTransform.height = paperWidth / aspectRatio3;
          // Centralizar verticalmente (corta partes superior/inferior)
          newTransform.x = 0;
          newTransform.y = (paperHeight - newTransform.height) / 2;
        }
        break;
        
      default:
        return;
    }
    
    // Aplicar a nova transformação
    updateImageTransform(selectedImageId, newTransform);
    saveHistoryState();
    
    console.log(`Tool clicked: ${toolId}`, newTransform);
  }, [selectedImageId, uploadedImages, updateImageTransform, saveHistoryState]);

  // Função para obter posição do mouse (usando o hook de scaling)
  const getMousePos = useCallback((canvas, event) => {
    return getMousePosition(event);
  }, [getMousePosition]);

  // Função para calcular o bounding box transformado
  const getTransformedBoundingBox = useCallback((transform) => {
    const cos = Math.cos(transform.rotation * Math.PI / 180);
    const sin = Math.sin(transform.rotation * Math.PI / 180);
    
    const w = transform.width;
    const h = transform.height;
    // Usar coordenadas diretas (já ajustadas se necessário)
    const centerX = transform.x + w / 2;
    const centerY = transform.y + h / 2;
    
    // Pontos dos cantos em coordenadas locais
    const localCorners = [
      { x: -w/2, y: -h/2 }, // NW
      { x: w/2, y: -h/2 },  // NE
      { x: w/2, y: h/2 },   // SE
      { x: -w/2, y: h/2 }   // SW
    ];
    
    // Transformar para coordenadas mundiais
    const worldCorners = localCorners.map(corner => ({
      x: corner.x * cos - corner.y * sin + centerX,
      y: corner.x * sin + corner.y * cos + centerY
    }));
    
    return worldCorners;
  }, []);

  // Função para verificar se um ponto está dentro de um polígono
  const pointInPolygon = useCallback((point, polygon) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      if (((polygon[i].y > point.y) !== (polygon[j].y > point.y)) &&
          (point.x < (polygon[j].x - polygon[i].x) * (point.y - polygon[i].y) / (polygon[j].y - polygon[i].y) + polygon[i].x)) {
        inside = !inside;
      }
    }
    return inside;
  }, []);



  // Função para desenhar o ponto de controle de efeito
  const drawEffectControlPoint = useCallback((ctx) => {
    if (!isEffectsPanelOpen || !effectsEditingImage || !activeEffectType) return;
    
    const needsPointControl = ['lensFlare', 'lensFlareOnly', 'anamorphicFlare', 'lensFlareStrong', 'lensFlareFun', 'zoomBlur', 'magicParticles', 'magicParticlesV2'].includes(activeEffectType);
    if (!needsPointControl) return;
    
    // 🎯 Não desenhar ponto verde do zoom blur quando na aba de transição
    if (activeEffectType === 'zoomBlur' && zoomBlurActiveTab === 'transition') return;
    
    const currentSettings = effectSettings[activeEffectType] || {};
    let posX, posY;
    
    if (activeEffectType === 'zoomBlur') {
      posX = currentSettings.centerX ?? 0.5;
      posY = currentSettings.centerY ?? 0.5;
    } else if (activeEffectType === 'lensFlare' || activeEffectType === 'lensFlareOnly' || activeEffectType === 'anamorphicFlare' || activeEffectType === 'lensFlareStrong' || activeEffectType === 'lensFlareFun') {
      posX = currentSettings.mouseX ?? 0.5;
      posY = currentSettings.mouseY ?? 0.5;
    } else if (activeEffectType === 'magicParticles' || activeEffectType === 'magicParticlesV2') {
      posX = currentSettings.positionX ?? 0.5;
      posY = currentSettings.positionY ?? 0.5;
    }
    
    const imageTransform = effectsEditingImage.transform;
    const imageLeft = paperPosition.x + (imageTransform.x * paperPosition.scale);
    const imageTop = paperPosition.y + (imageTransform.y * paperPosition.scale);
    const imageWidth = imageTransform.width * paperPosition.scale;
    const imageHeight = imageTransform.height * paperPosition.scale;
    
    // 🔧 IMPORTANTE: Inverter Y para exibir corretamente no canvas
    // Como os shaders usam flipVertical, os valores salvos estão invertidos
    const displayPosY = (activeEffectType === 'lensFlare' || activeEffectType === 'lensFlareOnly' || activeEffectType === 'anamorphicFlare' || activeEffectType === 'lensFlareStrong' || activeEffectType === 'lensFlareFun' || activeEffectType === 'zoomBlur')
      ? 1.0 - posY
      : posY;
    
    // Calcular posição do ponto no canvas
    const pointX = imageLeft + (posX * imageWidth);
    const pointY = imageTop + (displayPosY * imageHeight);
    
    // Cor verde padrão para todos os efeitos (padronizado)
    const color = '#22c55e'; // Verde para todos os controles de efeito
    
    // Desenhar crosshair (linhas de mira)
    ctx.save();
    ctx.strokeStyle = color + '40'; // 25% opacity
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);
    
    // Linha horizontal
    ctx.beginPath();
    ctx.moveTo(imageLeft, pointY);
    ctx.lineTo(imageLeft + imageWidth, pointY);
    ctx.stroke();
    
    // Linha vertical
    ctx.beginPath();
    ctx.moveTo(pointX, imageTop);
    ctx.lineTo(pointX, imageTop + imageHeight);
    ctx.stroke();
    
    ctx.setLineDash([]);
    ctx.restore();
    
    // Desenhar ponto central
    ctx.save();
    
    // Círculo externo (anel branco)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(pointX, pointY, 12, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    
    // Círculo interno (ponto colorido)
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(pointX, pointY, 4, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.restore();
  }, [isEffectsPanelOpen, effectsEditingImage, activeEffectType, effectSettings, paperPosition, zoomBlurActiveTab]);

  // 🎯 Função para desenhar círculos concêntricos de transição do Zoom Blur
  const drawTransitionCircles = useCallback((ctx) => {
    if (!isEffectsPanelOpen || !effectsEditingImage || activeEffectType !== 'zoomBlur') return;
    if (zoomBlurActiveTab !== 'transition') return;
    
    const currentSettings = effectSettings.zoomBlur || {};
    if (!currentSettings.transitionEnabled) return;
    
    // 🎯 Usar centro do efeito (não há mais transitionCenterX/Y separado)
    const { transitionRadius = 0.5, transitionFeather = 0.2, centerX: effectCenterX = 0.5, centerY: effectCenterY = 0.5 } = currentSettings;
    
    const imageTransform = effectsEditingImage.transform;
    const imageLeft = paperPosition.x + (imageTransform.x * paperPosition.scale);
    const imageTop = paperPosition.y + (imageTransform.y * paperPosition.scale);
    const imageWidth = imageTransform.width * paperPosition.scale;
    const imageHeight = imageTransform.height * paperPosition.scale;
    
    // Inverter Y para exibir corretamente (shader usa flipVertical)
    const displayPosY = 1.0 - effectCenterY;
    
    // Calcular centro em pixels
    const centerPosX = imageLeft + (effectCenterX * imageWidth);
    const centerPosY = imageTop + (displayPosY * imageHeight);
    
    // Calcular raios para CÍRCULO PERFEITO (mesmo raio em X e Y)
    const minDimension = Math.min(imageWidth, imageHeight);
    
    // Raio visual: círculo perfeito baseado na menor dimensão
    const outerRadius = transitionRadius * minDimension / 2;
    const innerRadius = outerRadius * (1.0 - transitionFeather);
    
    ctx.save();
    
    // Círculo EXTERNO (raio da transição) - PERFEITAMENTE REDONDO
    ctx.strokeStyle = '#8b5cf6'; // Roxo
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.arc(centerPosX, centerPosY, outerRadius, 0, Math.PI * 2);
    ctx.stroke();
    
    // Círculo INTERNO (suavidade) - PERFEITAMENTE REDONDO
    ctx.strokeStyle = '#a78bfa'; // Roxo claro
    ctx.lineWidth = 2;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.arc(centerPosX, centerPosY, innerRadius, 0, Math.PI * 2);
    ctx.stroke();
    
    ctx.setLineDash([]);
    
    // Desenhar ponto central (para mover ambos os círculos)
    const isCenterHovered = hoveredTransitionControl?.type === 'center';
    const centerSize = isCenterHovered ? 12 : 8;
    
    if (isCenterHovered) {
      // Glow effect no hover
      ctx.shadowColor = 'rgba(139, 92, 246, 0.8)';
      ctx.shadowBlur = 15;
    }
    
    ctx.fillStyle = isCenterHovered ? 'rgba(139, 92, 246, 1)' : 'rgba(139, 92, 246, 0.95)';
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = isCenterHovered ? 3 : 2;
    ctx.beginPath();
    ctx.arc(centerPosX, centerPosY, centerSize, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    
    if (isCenterHovered) {
      ctx.shadowBlur = 0;
    }
    
    // Desenhar 4 pontos de controle no círculo EXTERNO
    const outerAngles = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2]; // 0°, 90°, 180°, 270°
    outerAngles.forEach((angle) => {
      const px = centerPosX + outerRadius * Math.cos(angle);
      const py = centerPosY + outerRadius * Math.sin(angle);
      
      const isHovered = hoveredTransitionControl?.type === 'outer' && hoveredTransitionControl?.angle === angle;
      const pointSize = isHovered ? 10 : 6;
      
      if (isHovered) {
        // Glow effect no hover
        ctx.shadowColor = 'rgba(139, 92, 246, 0.8)';
        ctx.shadowBlur = 12;
      }
      
      ctx.fillStyle = isHovered ? 'rgba(139, 92, 246, 1)' : 'rgba(139, 92, 246, 0.8)';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = isHovered ? 3 : 2;
      ctx.beginPath();
      ctx.arc(px, py, pointSize, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      
      if (isHovered) {
        ctx.shadowBlur = 0;
      }
    });
    
    // Desenhar 4 pontos de controle no círculo INTERNO
    const innerAngles = [Math.PI / 4, (3 * Math.PI) / 4, (5 * Math.PI) / 4, (7 * Math.PI) / 4]; // 45°, 135°, 225°, 315°
    innerAngles.forEach((angle) => {
      const px = centerPosX + innerRadius * Math.cos(angle);
      const py = centerPosY + innerRadius * Math.sin(angle);
      
      const isHovered = hoveredTransitionControl?.type === 'inner' && hoveredTransitionControl?.angle === angle;
      const pointSize = isHovered ? 10 : 6;
      
      if (isHovered) {
        // Glow effect no hover
        ctx.shadowColor = 'rgba(167, 139, 250, 0.8)';
        ctx.shadowBlur = 12;
      }
      
      ctx.fillStyle = isHovered ? 'rgba(167, 139, 250, 1)' : 'rgba(167, 139, 250, 0.8)';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = isHovered ? 3 : 2;
      ctx.beginPath();
      ctx.arc(px, py, pointSize, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      
      if (isHovered) {
        ctx.shadowBlur = 0;
      }
    });
    
    ctx.restore();
  }, [isEffectsPanelOpen, effectsEditingImage, activeEffectType, zoomBlurActiveTab, effectSettings, paperPosition, hoveredTransitionControl]);

  // Função para desenhar todas as imagens
  const drawImages = useCallback((ctx) => {
    // Limpar canvas
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    // Log resumido apenas quando ordem realmente muda
    const orderKey = uploadedImages.map(img => img.id).join(',');
    if (window._lastRenderOrder !== orderKey) {
      console.log('🎨 [FluidCanvas] Ordem de renderização (índice 0 = fundo, último = frente):');
      uploadedImages.forEach((img, i) => {
        const position = i === 0 ? 'FUNDO' : (i === uploadedImages.length - 1 ? 'FRENTE' : 'MEIO');
        console.log(`  [${i}] ${position}: ${img.name || img.id.toString().substring(0, 15)}`);
      });
      window._lastRenderOrder = orderKey;
    }

    // Desenhar fundo da área de trabalho
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = devicePixelRatio;
    const canvasWidth = canvas.width / dpr;
    const canvasHeight = canvas.height / dpr;

    // Desenhar fundo (sempre em tamanho normal, sem zoom)
    ctx.fillStyle = '#252722';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // 🔍 APLICAR ZOOM apenas no conteúdo (folha + imagens)
    // Salvar estado antes do zoom
    ctx.save();

    // Centralizar o ponto de origem para o zoom
    // No desktop, considerar 320px da sidebar à esquerda para centralização visual
    const isDesktop = window.innerWidth >= 768;
    const sidebarWidth = 320;
    
    let centerX, centerY;
    if (isDesktop) {
      // Área disponível após a sidebar
      const availableWidth = canvasWidth - sidebarWidth;
      // Centro visual na área disponível
      centerX = sidebarWidth + availableWidth / 2;
      centerY = canvasHeight / 2;
    } else {
      // No mobile, centralizar normalmente
      centerX = canvasWidth / 2;
      centerY = canvasHeight / 2;
    }

    // Aplicar transformação de zoom centralizado (usando zoom animado)
    ctx.translate(centerX, centerY);
    ctx.scale(animatedZoom, animatedZoom);
    ctx.translate(-centerX, -centerY);

    // Desenhar imagens carregadas (a primeira imagem define o tamanho do papel)
    if (uploadedImages.length > 0) {
      const firstImage = uploadedImages[0];
      if (firstImage && firstImage.image) {
        // Aplicar clipping para limitar desenho à área da primeira imagem
        ctx.save();
        ctx.beginPath();
        ctx.rect(paperPosition.x, paperPosition.y, paperPosition.width, paperPosition.height);
        ctx.clip();
      
      // 🔧 ORDEM DE RENDERIZAÇÃO CORRIGIDA:
      // Index 0 = fundo (renderizado primeiro, embaixo)
      // Último index = frente (renderizado por último, em cima)
      // NÃO inverter aqui - o LayerList já controla a ordem visual

      uploadedImages.forEach((imageData, renderIndex) => {
        if (!imageData.isVisible) return;

        const { image, transform, opacity, blendMode, id } = imageData;

        // 🎨 Verificar se essa é a imagem sendo editada com efeito
        const isImageBeingEditedWithEffect = isEffectsPanelOpen && effectsEditingImage && effectsEditingImage.id === id;
        
        // 🎨 Verificar se essa é a imagem sendo editada com filtro de cor (NOVO SISTEMA)
        const isImageBeingEditedWithColorFilter = isColorFiltersPanelOpen && colorFilterEditingImage && colorFilterEditingImage.id === id;
        
        // Posicionar imagem na folha (com offset da posição da folha e escala)
        const imageX = paperPosition.x + (transform.x * paperPosition.scale);
        const imageY = paperPosition.y + (transform.y * paperPosition.scale);

        // 🎨 CASO 1: Filtro de cor ativo - desenhar imagem original + camada de cor separadamente
        if (isImageBeingEditedWithColorFilter && activeColorFilterCanvas && activeColorFilterType && colorFilterSettings[activeColorFilterType]) {
          const filterSettings = colorFilterSettings[activeColorFilterType];
          // Se opacidade for 0, usar 1% internamente para evitar comportamento estranho
          const filterOpacity = Math.max(1, filterSettings.opacity || 100);
          
          // 1️⃣ Desenhar imagem original com configurações da camada
          ctx.save();
          ctx.globalAlpha = (opacity || 100) / 100;
          ctx.globalCompositeOperation = blendMode || 'normal';
          
          ctx.translate(imageX + (transform.width * paperPosition.scale) / 2, imageY + (transform.height * paperPosition.scale) / 2);
          ctx.rotate((transform.rotation || 0) * Math.PI / 180);
          ctx.translate(-(transform.width * paperPosition.scale) / 2, -(transform.height * paperPosition.scale) / 2);
          ctx.drawImage(image, 0, 0, transform.width * paperPosition.scale, transform.height * paperPosition.scale);
          ctx.restore();
          
          // 2️⃣ Desenhar camada de cor POR CIMA com blendMode do filtro
          ctx.save();
          ctx.globalAlpha = filterOpacity / 100;
          ctx.globalCompositeOperation = filterSettings.blendMode || 'normal';
          
          ctx.translate(imageX + (transform.width * paperPosition.scale) / 2, imageY + (transform.height * paperPosition.scale) / 2);
          ctx.rotate((transform.rotation || 0) * Math.PI / 180);
          ctx.scale(1, -1); // WebGL tem coordenadas Y invertidas
          ctx.drawImage(activeColorFilterCanvas, -(transform.width * paperPosition.scale) / 2, -(transform.height * paperPosition.scale) / 2, transform.width * paperPosition.scale, transform.height * paperPosition.scale);
          ctx.restore();
        }
        // 🎨 CASO 2: Efeito ativo - desenhar canvas do efeito
        else if (isImageBeingEditedWithEffect && activeEffectCanvas) {
          ctx.save();
          ctx.globalAlpha = (opacity || 100) / 100;
          ctx.globalCompositeOperation = blendMode || 'normal';
          
          ctx.translate(imageX + (transform.width * paperPosition.scale) / 2, imageY + (transform.height * paperPosition.scale) / 2);
          ctx.rotate((transform.rotation || 0) * Math.PI / 180);
          ctx.scale(1, -1); // WebGL tem coordenadas Y invertidas
          ctx.drawImage(activeEffectCanvas, -(transform.width * paperPosition.scale) / 2, -(transform.height * paperPosition.scale) / 2, transform.width * paperPosition.scale, transform.height * paperPosition.scale);
          ctx.restore();
        }
        // 🎨 CASO 3: Imagem normal - desenhar apenas a imagem
        else {
          // 🔧 VERIFICAÇÃO: Garantir que a imagem está carregada antes de desenhar
          if (!image || !image.complete || image.naturalWidth === 0) {
            console.warn('⚠️ [FluidCanvas] Imagem não carregada completamente:', {
              id,
              hasImage: !!image,
              complete: image?.complete,
              naturalWidth: image?.naturalWidth,
              src: image?.src?.substring(0, 100)
            });
            return; // Skip esta imagem neste frame
          }

          try {
            ctx.save();
            ctx.globalAlpha = (opacity || 100) / 100;
            ctx.globalCompositeOperation = blendMode || 'normal';

            ctx.translate(imageX + (transform.width * paperPosition.scale) / 2, imageY + (transform.height * paperPosition.scale) / 2);
            ctx.rotate((transform.rotation || 0) * Math.PI / 180);
            ctx.translate(-(transform.width * paperPosition.scale) / 2, -(transform.height * paperPosition.scale) / 2);
            ctx.drawImage(image, 0, 0, transform.width * paperPosition.scale, transform.height * paperPosition.scale);
            ctx.restore();
          } catch (error) {
            console.error('❌ [FluidCanvas] Erro ao desenhar imagem:', {
              id,
              error: error.message,
              imageSrc: image?.src?.substring(0, 100)
            });
            ctx.restore();
          }
        }
      });
      
        // Restaurar contexto (remover clipping)
        ctx.restore();
      }
    }
    
    // Desenhar handles de imagens (apenas desktop - oculto no mobile)
    // TODO: Migrar para ControlsOverlayCanvas quando estiver estável
    const isMobile = window.innerWidth < 768;
    if (!isMobile) {
      uploadedImages.forEach(imageData => {
        if (imageData.id === selectedImageId) {
        }
      });
    }
    
    // 🎨 REFATORADO: Controles agora são desenhados no ControlsOverlayCanvas
    // drawEffectControlPoint(ctx);
    // drawTransitionCircles(ctx);

    // 🔍 Restaurar estado do contexto (remover zoom)
    ctx.restore();
  }, [uploadedImages, selectedImageId, paperPosition, isEffectsPanelOpen, effectsEditingImage, activeEffectCanvas, effectUpdateCounter, isColorFiltersPanelOpen, colorFilterEditingImage, activeColorFilterCanvas, activeColorFilterType, colorFilterSettings, colorFilterUpdateCounter, animatedZoom, activeEffectType, zoomBlurActiveTab]);

  // Função para calcular nova transformação durante edição
  const calculateNewTransform = useCallback((imageData, startPos, currentPos, editType, handle, isIncremental = false) => {
    const { transform } = imageData;
    
    // Converter coordenadas do canvas para coordenadas relativas à folha
    const canvasToPaper = (pos) => ({
      x: (pos.x - paperPosition.x) / paperPosition.scale,
      y: (pos.y - paperPosition.y) / paperPosition.scale
    });
    
    const startPosPaper = canvasToPaper(startPos);
    const currentPosPaper = canvasToPaper(currentPos);
    const lastPosPaper = lastMousePosRef.current ? canvasToPaper(lastMousePosRef.current) : null;
    
    let deltaX, deltaY;
    
    if (isIncremental && lastPosPaper) {
      // Movimento incremental - mais suave
      deltaX = currentPosPaper.x - lastPosPaper.x;
      deltaY = currentPosPaper.y - lastPosPaper.y;
    } else {
      // Movimento absoluto - para início da operação
      deltaX = currentPosPaper.x - startPosPaper.x;
      deltaY = currentPosPaper.y - startPosPaper.y;
    }

    switch (editType) {
      case 'move':
        return {
          ...transform,
          x: transform.x + deltaX,
          y: transform.y + deltaY
        };

      case 'resize':
        const aspectRatio = transform.width / transform.height;
        let newWidth = transform.width;
        let newHeight = transform.height;
        let newX = transform.x;
        let newY = transform.y;

        // Para redimensionamento, usar movimento incremental para melhor sensibilidade
        let resizeDeltaX, resizeDeltaY;
        
        if (isIncremental && lastMousePosRef.current) {
          // Movimento incremental - mais suave e responsivo
          resizeDeltaX = currentPos.x - lastMousePosRef.current.x;
          resizeDeltaY = currentPos.y - lastMousePosRef.current.y;
        } else {
          // Movimento absoluto - para início da operação
          resizeDeltaX = currentPos.x - startPos.x;
          resizeDeltaY = currentPos.y - startPos.y;
        }

        // Determinar se é handle de canto (proporcional) ou lateral (não-proporcional)
        const isCornerHandle = ['nw', 'ne', 'sw', 'se'].includes(handle);
        const isSideHandle = ['n', 's', 'w', 'e'].includes(handle);

        if (isCornerHandle) {
          // Handles de canto (bolinhas) - redimensionamento proporcional
        switch (handle) {
          case 'se': // Southeast - redimensionar para direita e baixo
            newWidth = Math.max(20, transform.width + resizeDeltaX);
            newHeight = newWidth / aspectRatio;
            break;
          case 'sw': // Southwest - redimensionar para esquerda e baixo
            newWidth = Math.max(20, transform.width - resizeDeltaX);
            newHeight = newWidth / aspectRatio;
            newX = transform.x + (transform.width - newWidth);
            break;
          case 'ne': // Northeast - redimensionar para direita e cima
            newHeight = Math.max(20, transform.height - resizeDeltaY);
            newWidth = newHeight * aspectRatio;
            newY = transform.y + (transform.height - newHeight);
            break;
          case 'nw': // Northwest - redimensionar para esquerda e cima
            newHeight = Math.max(20, transform.height + resizeDeltaY);
            newWidth = newHeight * aspectRatio;
            newX = transform.x + (transform.width - newWidth);
            newY = transform.y + (transform.height - newHeight);
            break;
            default:
              break;
          }
        } else if (isSideHandle) {
          // Handles laterais (retângulos) - redimensionamento não-proporcional
          switch (handle) {
          case 'e': // East - redimensionar apenas largura para direita
            newWidth = Math.max(20, transform.width + resizeDeltaX);
              // Manter altura original (não-proporcional)
            break;
          case 'w': // West - redimensionar apenas largura para esquerda
            newWidth = Math.max(20, transform.width - resizeDeltaX);
            newX = transform.x + (transform.width - newWidth);
              // Manter altura original (não-proporcional)
            break;
          case 's': // South - redimensionar apenas altura para baixo
            newHeight = Math.max(20, transform.height + resizeDeltaY);
              // Manter largura original (não-proporcional)
            break;
          case 'n': // North - redimensionar apenas altura para cima
            newHeight = Math.max(20, transform.height - resizeDeltaY);
            newY = transform.y + (transform.height - newHeight);
              // Manter largura original (não-proporcional)
            break;
          default:
            break;
          }
        }

        return {
          ...transform,
          x: newX,
          y: newY,
          width: newWidth,
          height: newHeight
        };

      case 'rotate':
        const centerX = transform.x + transform.width / 2;
        const centerY = transform.y + transform.height / 2;
        const startAngle = Math.atan2(startPos.y - centerY, startPos.x - centerX);
        const currentAngle = Math.atan2(currentPos.y - centerY, currentPos.x - centerX);
        const angleDelta = (currentAngle - startAngle) * 180 / Math.PI;
        
        return {
          ...transform,
          rotation: (transform.rotation || 0) + angleDelta
        };

      default:
        return transform;
    }
  }, [paperPosition]);

  // Função para detectar qual handle está sendo clicado
  const getHandleAtPoint = useCallback((imageData, point) => {
    const { transform } = imageData;

    const cornerHandleRadius = 10; // Raio visual das bolinhas dos cantos
    const cornerHitRadius = 20; // 🆕 Raio de clique dobrado (área de hit maior)
    const sideHandleWidth = 22; // Largura visual dos retângulos laterais
    const sideHandleHeight = 8; // Altura visual dos retângulos laterais
    const sideHitAreaOffset = 12; // 🆕 Offset de clique aumentado de 6 para 12
    const rotationHandleSize = 24; // Handle de rotação visual
    const rotationHitMultiplier = 1.5; // 🆕 Área de clique 50% maior (36px total)
    const rotationHandleDistance = 40; // Distância do handle de rotação
    
    // Obter bounding box transformado
    const boundingBox = getTransformedBoundingBox(transform);

    // Handles de redimensionamento (cantos) - bolinhas nos cantos do bounding box
    const cornerHandles = boundingBox.map((corner, index) => {
      const names = ['nw', 'ne', 'se', 'sw'];
      return {
        name: names[index],
        x: corner.x - cornerHandleRadius,
        y: corner.y - cornerHandleRadius,
        centerX: corner.x, // 🆕 Armazenar centro real do handle
        centerY: corner.y, // 🆕 Armazenar centro real do handle
        type: 'corner'
      };
    });

    // Handles laterais (meio das bordas) - retângulos arredondados
    const sideHandles = [];
    for (let i = 0; i < boundingBox.length; i++) {
      const current = boundingBox[i];
      const next = boundingBox[(i + 1) % boundingBox.length];
      const midX = (current.x + next.x) / 2;
      const midY = (current.y + next.y) / 2;

      const names = ['n', 'e', 's', 'w'];
      sideHandles.push({
        name: names[i],
        x: midX - sideHandleWidth/2,
        y: midY - sideHandleHeight/2,
        centerX: midX, // 🆕 Armazenar centro real do handle
        centerY: midY, // 🆕 Armazenar centro real do handle
        type: 'side'
      });
    }
    
    // Handle de rotação - posicionado acima do centro do bounding box
    const centerX = boundingBox.reduce((sum, corner) => sum + corner.x, 0) / boundingBox.length;
    const centerY = boundingBox.reduce((sum, corner) => sum + corner.y, 0) / boundingBox.length;
    
    // Calcular direção "para cima" baseada na rotação
    const cos = Math.cos(transform.rotation * Math.PI / 180);
    const sin = Math.sin(transform.rotation * Math.PI / 180);
    const upX = -sin; // Direção "para cima" em coordenadas locais
    const upY = -cos;
    
    const rotationHandle = {
      name: 'rotate',
      x: centerX + upX * rotationHandleDistance - rotationHandleSize/2,
      y: centerY + upY * rotationHandleDistance - rotationHandleSize/2,
      radius: rotationHandleSize/2
    };
    
    // Verificar handles de redimensionamento com área de clique maior
    const allHandles = [...cornerHandles, ...sideHandles];
    for (const handle of allHandles) {
      if (handle.type === 'corner') {
        // 🆕 Detecção para bolinhas (cantos) usando centro armazenado
        const distance = Math.sqrt(
          Math.pow(point.x - handle.centerX, 2) +
          Math.pow(point.y - handle.centerY, 2)
        );
        if (distance <= cornerHitRadius) { // 🆕 Usando raio de hit maior (20px)
          return handle.name;
        }
      } else if (handle.type === 'side') {
        // 🆕 Detecção para retângulos (laterais) - PRECISA USAR MESMA LÓGICA DO DESENHO
        const isTopBottom = handle.name === 'n' || handle.name === 's';

        // Usar mesma lógica de tamanho que drawControls
        const handleWidth = isTopBottom ? sideHandleWidth * 1.5 : sideHandleWidth;
        const handleHeight = isTopBottom ? sideHandleHeight : sideHandleWidth;

        // 🆕 Área de clique expandida centralizada no centro real do handle
        const hitWidth = handleWidth + (sideHitAreaOffset * 2);
        const hitHeight = handleHeight + (sideHitAreaOffset * 2);
        const hitX = handle.centerX - hitWidth / 2;
        const hitY = handle.centerY - hitHeight / 2;

        if (point.x >= hitX && point.x <= hitX + hitWidth &&
            point.y >= hitY && point.y <= hitY + hitHeight) {
        return handle.name;
        }
      }
    }

    // 🆕 Verificar handle de rotação (círculo) com área de clique 50% maior
    const distance = Math.sqrt(
      Math.pow(point.x - (rotationHandle.x + rotationHandleSize/2), 2) +
      Math.pow(point.y - (rotationHandle.y + rotationHandleSize/2), 2)
    );
    if (distance <= rotationHandle.radius * rotationHitMultiplier) { // 🆕 Usando multiplicador maior
      return 'rotate';
    }
    
    return null;
  }, []);

  // Função para verificar se o ponto está dentro da imagem
  const isPointInImage = useCallback((imageData, point) => {
    const { transform, image } = imageData;

    // Verificar se o ponto está dentro da área da folha primeiro
    const isInPaperArea = point.x >= paperPosition.x &&
                         point.x <= paperPosition.x + paperPosition.width &&
                         point.y >= paperPosition.y &&
                         point.y <= paperPosition.y + paperPosition.height;

    if (!isInPaperArea) return false;

    // Criar transform com coordenadas ajustadas para o canvas
    const adjustedTransform = {
      ...transform,
      x: paperPosition.x + (transform.x * paperPosition.scale),
      y: paperPosition.y + (transform.y * paperPosition.scale),
      width: transform.width * paperPosition.scale,
      height: transform.height * paperPosition.scale
    };

    // Usar bounding box transformado para detecção inicial
    const boundingBox = getTransformedBoundingBox(adjustedTransform);
    if (!pointInPolygon(point, boundingBox)) return false;

    // 🆕 Verificação pixel-perfect: verificar se o pixel clicado é opaco
    if (!image) return true; // Se não há imagem, usar apenas bounding box

    // Converter ponto do canvas para coordenadas locais da imagem
    const localX = (point.x - adjustedTransform.x) / paperPosition.scale;
    const localY = (point.y - adjustedTransform.y) / paperPosition.scale;

    // Aplicar rotação inversa para obter coordenadas na imagem original
    const cos = Math.cos(-transform.rotation * Math.PI / 180);
    const sin = Math.sin(-transform.rotation * Math.PI / 180);
    const centerX = transform.width / 2;
    const centerY = transform.height / 2;

    const relX = localX - centerX;
    const relY = localY - centerY;

    const rotatedX = relX * cos - relY * sin + centerX;
    const rotatedY = relX * sin + relY * cos + centerY;

    // Verificar limites
    if (rotatedX < 0 || rotatedX >= transform.width ||
        rotatedY < 0 || rotatedY >= transform.height) {
      return false;
    }

    // Criar canvas off-screen para ler o pixel
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');

    try {
      ctx.drawImage(
        image,
        Math.floor(rotatedX), Math.floor(rotatedY), 1, 1,
        0, 0, 1, 1
      );

      const pixelData = ctx.getImageData(0, 0, 1, 1).data;
      const alpha = pixelData[3];

      // Considerar opaco se alpha > 10 (threshold baixo para capturar bordas suaves)
      return alpha > 10;
    } catch (error) {
      // Se falhar ao ler pixel (CORS, etc), usar apenas bounding box
      console.warn('Falha ao ler pixel:', error);
      return true;
    }
  }, [getTransformedBoundingBox, pointInPolygon, paperPosition]);

  // 🎯 Função para detectar hit nos controles dos círculos de transição
  const checkTransitionCircleHit = useCallback((mousePos) => {
    if (!isEffectsPanelOpen || !effectsEditingImage || !activeEffectType) return null;
    
    // Verificar se efeito suporta transição
    const EFFECTS_WITH_TRANSITION = ['zoomBlur', 'gaussianBlur', 'ditherBlur', 'hatchBlur', 'ditherBayer', 'dawnBringer', 'pixelArtPalette'];
    if (!EFFECTS_WITH_TRANSITION.includes(activeEffectType)) return null;
    
    // Verificar se está na aba de transição
    const currentTab = effectActiveTab[activeEffectType] || 'effect';
    if (currentTab !== 'transition') return null;
    
    const currentSettings = effectSettings[activeEffectType] || {};
    if (!currentSettings.transitionEnabled) return null;
    
    // 🎯 Usar centro do efeito (sincronizado)
    const { transitionRadius = 0.5, transitionFeather = 0.2, centerX: effectCenterX = 0.5, centerY: effectCenterY = 0.5 } = currentSettings;
    
    const imageTransform = effectsEditingImage.transform;
    const imageLeft = paperPosition.x + (imageTransform.x * paperPosition.scale);
    const imageTop = paperPosition.y + (imageTransform.y * paperPosition.scale);
    const imageWidth = imageTransform.width * paperPosition.scale;
    const imageHeight = imageTransform.height * paperPosition.scale;
    
    const centerPosX = imageLeft + (effectCenterX * imageWidth);
    const centerPosY = imageTop + (effectCenterY * imageHeight);
    
    // Calcular raios para CÍRCULO PERFEITO
    const minDimension = Math.min(imageWidth, imageHeight);
    const outerRadius = transitionRadius * minDimension / 2;
    const innerRadius = outerRadius * (1.0 - transitionFeather);
    
    const hitRadius = 18; // Raio de detecção dos pontos
    
    // Distância do mouse ao centro
    const distToCenter = Math.sqrt(
      Math.pow(mousePos.x - centerPosX, 2) + Math.pow(mousePos.y - centerPosY, 2)
    );
    
    // PRIORIDADE 1: Verificar hit nos pontos do círculo externo (mais específico)
    const outerAngles = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2];
    for (const angle of outerAngles) {
      const px = centerPosX + outerRadius * Math.cos(angle);
      const py = centerPosY + outerRadius * Math.sin(angle);
      const dist = Math.sqrt(Math.pow(mousePos.x - px, 2) + Math.pow(mousePos.y - py, 2));
      if (dist <= hitRadius) {
        return { type: 'outer', angle };
      }
    }
    
    // PRIORIDADE 2: Verificar hit nos pontos do círculo interno
    const innerAngles = [Math.PI / 4, (3 * Math.PI) / 4, (5 * Math.PI) / 4, (7 * Math.PI) / 4];
    for (const angle of innerAngles) {
      const px = centerPosX + innerRadius * Math.cos(angle);
      const py = centerPosY + innerRadius * Math.sin(angle);
      const dist = Math.sqrt(Math.pow(mousePos.x - px, 2) + Math.pow(mousePos.y - py, 2));
      if (dist <= hitRadius) {
        return { type: 'inner', angle };
      }
    }
    
    // PRIORIDADE 3: Verificar se está dentro da área do círculo (para mover)
    // Permite arrastar de qualquer parte do círculo, não apenas do centro
    if (distToCenter <= outerRadius) {
      return { type: 'center', angle: null };
    }
    
    return null;
  }, [isEffectsPanelOpen, effectsEditingImage, activeEffectType, effectActiveTab, effectSettings, paperPosition]);

  // Event handlers do mouse
  const handleMouseDown = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // 🎨 PRIORIDADE MÁXIMA: Se está no modo de pintura, não processar eventos aqui
    if (isSimplePaintingMode) {
      console.log('🎨 [FluidCanvas] Modo pintura ativo - ignorando eventos do canvas principal');
      return;
    }

    const pos = getMousePos(canvas, event);

    // 🎯 PRIORIDADE 1: Verificar se clicou nos círculos de transição do Zoom Blur
    const transitionHit = checkTransitionCircleHit(pos);
    if (transitionHit) {
      draggingTransitionRef.current = { type: transitionHit.type, angle: transitionHit.angle };
      setDraggingTransitionControl(transitionHit.type);
      setDraggingTransitionAngle(transitionHit.angle);
      event.preventDefault();
      event.stopPropagation();
      canvas.style.cursor = transitionHit.type === 'center' ? 'move' : 'pointer';
      return; // BLOQUEAR todos os outros eventos
    }

    // 🎯 PRIORIDADE 2: Verificar se clicou no ponto de controle de efeito
    if (isEffectsPanelOpen && effectsEditingImage && activeEffectType) {
      const needsPointControl = ['lensFlare', 'lensFlareOnly', 'anamorphicFlare', 'lensFlareStrong', 'lensFlareFun', 'zoomBlur', 'magicParticles', 'magicParticlesV2'].includes(activeEffectType);
      
      if (needsPointControl) {
        const currentSettings = effectSettings[activeEffectType] || {};
        let posX, posY;
        
        if (activeEffectType === 'zoomBlur') {
          posX = currentSettings.centerX ?? 0.5;
          posY = currentSettings.centerY ?? 0.5;
        } else if (activeEffectType === 'lensFlare' || activeEffectType === 'lensFlareOnly' || activeEffectType === 'anamorphicFlare' || activeEffectType === 'lensFlareStrong' || activeEffectType === 'lensFlareFun') {
          posX = currentSettings.mouseX ?? 0.5;
          posY = currentSettings.mouseY ?? 0.5;
        } else if (activeEffectType === 'magicParticles' || activeEffectType === 'magicParticlesV2') {
          posX = currentSettings.positionX ?? 0.5;
          posY = currentSettings.positionY ?? 0.5;
        }
        
        const imageTransform = effectsEditingImage.transform;
        const imageLeft = paperPosition.x + (imageTransform.x * paperPosition.scale);
        const imageTop = paperPosition.y + (imageTransform.y * paperPosition.scale);
        const imageWidth = imageTransform.width * paperPosition.scale;
        const imageHeight = imageTransform.height * paperPosition.scale;
        
        // 🔧 IMPORTANTE: Inverter Y para detectar clique corretamente
        const displayPosY = (activeEffectType === 'lensFlare' || activeEffectType === 'lensFlareOnly' || activeEffectType === 'anamorphicFlare' || activeEffectType === 'lensFlareStrong' || activeEffectType === 'lensFlareFun' || activeEffectType === 'zoomBlur')
          ? 1.0 - posY
          : posY;
        
        const pointX = imageLeft + (posX * imageWidth);
        const pointY = imageTop + (displayPosY * imageHeight);
        
        // Verificar se clicou no ponto (raio de 15px para facilitar o clique)
        const distance = Math.sqrt(Math.pow(pos.x - pointX, 2) + Math.pow(pos.y - pointY, 2));
        if (distance <= 15) {
          isDraggingPointRef.current = true;
          setIsDraggingPoint(true);
          return; // Não processar outros eventos
        }
      }
    }

    // 🆕 Verificar se clicou em uma imagem (iterar de trás para frente - última camada primeiro)
    let clickedImage = null;
    for (let i = uploadedImages.length - 1; i >= 0; i--) {
      if (isPointInImage(uploadedImages[i], pos)) {
        clickedImage = uploadedImages[i];
        break;
      }
    }
      
      if (clickedImage) {
        setSelectedImage(clickedImage.id);
        
        // Mostrar toolbar flutuante
        const toolbarData = calculateToolbarPosition(clickedImage);
        setToolbarPosition({ x: toolbarData.x, y: toolbarData.y });
        setToolbarDirection(toolbarData.direction);
        setToolbarVisible(true);
      
      // Removido: controles de transformação desabilitados para MVP
      // Apenas selecionar a imagem, sem permitir movimento/redimensionamento/rotação
    } else {
      setSelectedImage(null);
      setToolbarVisible(false);
    }
  }, [uploadedImages, setSelectedImage, isPointInImage, calculateToolbarPosition, isEffectsPanelOpen, effectsEditingImage, activeEffectType, effectSettings, paperPosition, isSimplePaintingMode]);

  const handleMouseMove = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // 🎨 PRIORIDADE MÁXIMA: Se está no modo de pintura, não processar eventos aqui
    // Nota: O cursor crosshair é gerenciado pelo SimplePaintingCanvas, não aqui
    if (isSimplePaintingMode) {
      return;
    }
    
    const pos = getMousePos(canvas, event);

    // 🎯 PRIORIDADE MÁXIMA: Se está arrastando controles dos círculos de transição
    const EFFECTS_WITH_TRANSITION = ['zoomBlur', 'gaussianBlur', 'ditherBlur', 'hatchBlur', 'ditherBayer', 'dawnBringer', 'pixelArtPalette'];
    if (draggingTransitionRef.current.type && isEffectsPanelOpen && effectsEditingImage && EFFECTS_WITH_TRANSITION.includes(activeEffectType)) {
      const currentSettings = effectSettings[activeEffectType] || {};
      const imageTransform = effectsEditingImage.transform;
      const imageLeft = paperPosition.x + (imageTransform.x * paperPosition.scale);
      const imageTop = paperPosition.y + (imageTransform.y * paperPosition.scale);
      const imageWidth = imageTransform.width * paperPosition.scale;
      const imageHeight = imageTransform.height * paperPosition.scale;
      
      if (draggingTransitionRef.current.type === 'center') {
        // Arrastar centro - move o centro do efeito (que também é centro da transição)
        const relativeX = (pos.x - imageLeft) / imageWidth;
        const relativeY = (pos.y - imageTop) / imageHeight;
        
        const newX = Math.max(0, Math.min(1, relativeX));
        const newY = Math.max(0, Math.min(1, relativeY));
        
        // Atualização direta (síncrona) para zero delay
        updateEffectSettings(activeEffectType, {
          centerX: newX,
          centerY: newY
        });
        
        event.preventDefault();
        return; // Não processar outros eventos
        
      } else if (draggingTransitionRef.current.type === 'outer') {
        // Arrastar ponto externo - ajusta raio (círculo perfeito)
        const currentCenterX = currentSettings.centerX ?? 0.5;
        const currentCenterY = currentSettings.centerY ?? 0.5;
        const centerX = imageLeft + (currentCenterX * imageWidth);
        const centerY = imageTop + (currentCenterY * imageHeight);
        
        const minDimension = Math.min(imageWidth, imageHeight);
        
        // Calcular distância euclidiana simples (círculo perfeito visual)
        const dist = Math.sqrt(Math.pow(pos.x - centerX, 2) + Math.pow(pos.y - centerY, 2));
        
        // Converter para raio normalizado (0-1)
        const newRadius = Math.max(0.05, Math.min(1.5, dist / (minDimension / 2)));
        
        // Atualização direta (síncrona) para zero delay
        updateEffectSettings(activeEffectType, {
          transitionRadius: newRadius
        });
        
        event.preventDefault();
        return; // Não processar outros eventos
        
      } else if (draggingTransitionRef.current.type === 'inner') {
        // Arrastar ponto interno - ajusta suavidade (feather)
        const currentCenterX = currentSettings.centerX ?? 0.5;
        const currentCenterY = currentSettings.centerY ?? 0.5;
        const currentRadius = currentSettings.transitionRadius ?? 0.5;
        const centerX = imageLeft + (currentCenterX * imageWidth);
        const centerY = imageTop + (currentCenterY * imageHeight);
        
        const minDimension = Math.min(imageWidth, imageHeight);
        
        // Calcular distância euclidiana simples (círculo perfeito visual)
        const dist = Math.sqrt(Math.pow(pos.x - centerX, 2) + Math.pow(pos.y - centerY, 2));
        const outerRadius = currentRadius * minDimension / 2;
        
        // Feather = 1 - (distância ao centro / raio externo)
        const feather = Math.max(0, Math.min(1, 1.0 - (dist / outerRadius)));
        
        // Atualização direta (síncrona) para zero delay
        updateEffectSettings(activeEffectType, {
          transitionFeather: feather
        });
        
        event.preventDefault();
        return; // Não processar outros eventos
      }
      
      event.preventDefault();
      return; // BLOQUEAR outros eventos
    }

    // 🎯 PRIORIDADE 2: Se está arrastando o ponto de controle de efeito
    if (isDraggingPointRef.current && isEffectsPanelOpen && effectsEditingImage && activeEffectType) {
      const needsPointControl = ['lensFlare', 'lensFlareOnly', 'anamorphicFlare', 'lensFlareStrong', 'lensFlareFun', 'zoomBlur', 'magicParticles', 'magicParticlesV2'].includes(activeEffectType);
      
      // Não processar se estiver arrastando controles de transição
      if (draggingTransitionRef.current.type) {
        return;
      }
      
      if (needsPointControl) {
        const imageTransform = effectsEditingImage.transform;
        const imageLeft = paperPosition.x + (imageTransform.x * paperPosition.scale);
        const imageTop = paperPosition.y + (imageTransform.y * paperPosition.scale);
        const imageWidth = imageTransform.width * paperPosition.scale;
        const imageHeight = imageTransform.height * paperPosition.scale;
        
        // Calcular posição normalizada (0-1) dentro da imagem
        const relativeX = (pos.x - imageLeft) / imageWidth;
        let relativeY = (pos.y - imageTop) / imageHeight;
        
        // 🔧 IMPORTANTE: Inverter Y para efeitos que usam flipVertical no shader
        // Lens Flare, Anamorphic Flare e Zoom Blur usam flipVertical: true, então precisamos inverter aqui também
        // Magic Particles (v1 e v2) NÃO usam flipVertical porque os shaders já trabalham com coordenadas Y normais
        if (activeEffectType === 'lensFlare' || activeEffectType === 'lensFlareOnly' || activeEffectType === 'anamorphicFlare' || activeEffectType === 'lensFlareStrong' || activeEffectType === 'lensFlareFun' || activeEffectType === 'zoomBlur') {
          relativeY = 1.0 - relativeY;
        }
        
        // Clampar entre 0 e 1
        const newX = Math.max(0, Math.min(1, relativeX));
        const newY = Math.max(0, Math.min(1, relativeY));
        
        // Determinar os nomes dos parâmetros
        let paramX, paramY;
        if (activeEffectType === 'zoomBlur') {
          paramX = 'centerX';
          paramY = 'centerY';
        } else if (activeEffectType === 'lensFlare' || activeEffectType === 'lensFlareOnly' || activeEffectType === 'anamorphicFlare' || activeEffectType === 'lensFlareStrong' || activeEffectType === 'lensFlareFun') {
          paramX = 'mouseX';
          paramY = 'mouseY';
        } else if (activeEffectType === 'magicParticles' || activeEffectType === 'magicParticlesV2') {
          paramX = 'positionX';
          paramY = 'positionY';
        }
        
        // Atualizar settings
        updateEffectSettings(activeEffectType, {
          [paramX]: newX,
          [paramY]: newY,
        });
        
        return; // Não processar outros eventos
      }
    }

    // 🚫 Se está arrastando controles de transição, não processar edição de imagem
    if (draggingTransitionRef.current.type) {
      return;
    }

    if (isImageEditingRef.current && editingImageIdRef.current) {
      const imageData = uploadedImages.find(img => img.id === editingImageIdRef.current);
      if (imageData) {
        // Usar requestAnimationFrame para todas as operações para melhor suavidade
      // Cancelar animação anterior se existir
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
        // Usar requestAnimationFrame para todas as operações
      animationFrameRef.current = requestAnimationFrame(() => {
      const newTransform = calculateNewTransform(
            imageData,
        imageEditStartRef.current,
        pos,
        imageEditTypeRef.current,
        imageEditHandleRef.current,
            true // Movimento incremental para todas as operações
      );
      
      updateImageTransform(editingImageIdRef.current, newTransform);
          lastMousePosRef.current = pos;
        });
        }
    } else {
      // 🆕 Atualizar cursor e hover baseado na posição do mouse
      // Primeiro verificar handles da imagem selecionada (prioridade), depois outras imagens
      let hoveredImage = null;
      let handle = null;
      
      // 🎯 PRIORIDADE: Detectar hover nos controles de transição ANTES de verificar imagens
      const transitionHover = checkTransitionCircleHit(pos);
      if (transitionHover) {
        setHoveredTransitionControl(transitionHover);
        // Definir cursor baseado no tipo de controle
        if (transitionHover.type === 'center') {
          canvas.style.cursor = 'move';
        } else if (transitionHover.type === 'outer') {
          canvas.style.cursor = 'ew-resize'; // Cursor de resize horizontal
        } else if (transitionHover.type === 'inner') {
          canvas.style.cursor = 'ns-resize'; // Cursor de resize vertical
        }
        return; // Não processar hover de imagens
      } else {
        setHoveredTransitionControl(null);
      }

      // Se há uma imagem selecionada, verificar seus handles primeiro
      if (selectedImageId) {
        const selectedImage = uploadedImages.find(img => img.id === selectedImageId);
        if (selectedImage) {
          const adjustedImageData = {
            ...selectedImage,
            transform: {
              ...selectedImage.transform,
              x: paperPosition.x + (selectedImage.transform.x * paperPosition.scale),
              y: paperPosition.y + (selectedImage.transform.y * paperPosition.scale),
              width: selectedImage.transform.width * paperPosition.scale,
              height: selectedImage.transform.height * paperPosition.scale
            }
          };
          handle = getHandleAtPoint(adjustedImageData, pos);

          // Se encontrou handle, usar essa imagem
          if (handle) {
            hoveredImage = selectedImage;
          }
        }
      }

      // Se não encontrou handle na selecionada, procurar em todas (reverso)
      if (!hoveredImage) {
        for (let i = uploadedImages.length - 1; i >= 0; i--) {
          const imageData = uploadedImages[i];
          const adjustedImageData = {
            ...imageData,
            transform: {
              ...imageData.transform,
              x: paperPosition.x + (imageData.transform.x * paperPosition.scale),
              y: paperPosition.y + (imageData.transform.y * paperPosition.scale),
              width: imageData.transform.width * paperPosition.scale,
              height: imageData.transform.height * paperPosition.scale
            }
          };

          // Verificar handles primeiro
          handle = getHandleAtPoint(adjustedImageData, pos);
          if (handle) {
            hoveredImage = imageData;
            break;
          }

          // Se não tem handle, verificar se está dentro da imagem
          if (isPointInImage(imageData, pos)) {
            hoveredImage = imageData;
            break;
          }
        }
      }

      if (hoveredImage) {
        // 🆕 Atualizar estado de hover
        setHoveredHandle(handle);
        setHoveredImageId(hoveredImage.id);

        if (handle) {
          if (handle === 'rotate') {
            canvas.style.cursor = 'grab';
          } else if (handle === 'nw') {
            canvas.style.cursor = 'nw-resize';
          } else if (handle === 'ne') {
            canvas.style.cursor = 'ne-resize';
          } else if (handle === 'sw') {
            canvas.style.cursor = 'sw-resize';
          } else if (handle === 'se') {
            canvas.style.cursor = 'se-resize';
          } else if (handle === 'n' || handle === 's') {
            canvas.style.cursor = 'ns-resize';
          } else if (handle === 'e' || handle === 'w') {
            canvas.style.cursor = 'ew-resize';
          } else {
            canvas.style.cursor = 'nw-resize';
          }
        } else {
          canvas.style.cursor = 'move';
        }
      } else {
        // 🆕 Limpar hover quando não está sobre nenhuma imagem
        setHoveredHandle(null);
        setHoveredImageId(null);
        canvas.style.cursor = 'default';
      }
    }
  }, [uploadedImages, calculateNewTransform, updateImageTransform, isPointInImage, getHandleAtPoint, isEffectsPanelOpen, effectsEditingImage, activeEffectType, paperPosition, updateEffectSettings, isSimplePaintingMode]);

  // 🖱️ Handler para zoom com scroll/trackpad (otimizado para macOS)
  const handleWheel = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // 🛡️ CRÍTICO: Se estiver em modo de pinch, sempre prevenir zoom do navegador
    if (isPinchingRef.current) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
    
    // Verificar se o mouse está realmente sobre o canvas
    const rect = canvas.getBoundingClientRect();
    const isOverCanvas = (
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
    );
    
    // Se não estiver sobre o canvas, não fazer nada (mas não prevenir o evento)
    if (!isOverCanvas) return;
    
    // Prevenir zoom do navegador quando estiver sobre o canvas
    event.preventDefault();
    event.stopPropagation();
    
    // Prevenir zoom do navegador com Ctrl/Cmd + Scroll (gesto de pinch no trackpad)
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      event.stopPropagation();
      // Não processar zoom se for gesto de pinch do navegador (já está sendo tratado pelo touch)
      return false;
    }
    
    // Detectar se é trackpad macOS (normalmente deltaMode === 0 e deltaY pode ser positivo/negativo)
    // No macOS, trackpad envia valores deltaY muito menores que mouse wheel
    const isTrackpad = event.deltaMode === 0 || Math.abs(event.deltaY) < 50;
    const isMacOS = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    
    // Calcular sensibilidade baseada no tipo de dispositivo
    let zoomSensitivity;
    if (isTrackpad || isMacOS) {
      // Trackpad macOS - valores deltaY são pequenos (geralmente -1 a 1, mas podem ser maiores)
      // Ajustar sensibilidade para valores típicos de trackpad macOS
      zoomSensitivity = 0.01; // Aumentado de 0.001 para melhor resposta no trackpad
    } else if (event.deltaMode === 1) {
      // Mouse wheel (linhas)
      zoomSensitivity = 0.05;
    } else {
      // Outros dispositivos
      zoomSensitivity = 0.001;
    }
    
    // Calcular delta do zoom
    // deltaY < 0 = scroll para cima (zoom in)
    // deltaY > 0 = scroll para baixo (zoom out)
    const zoomDelta = -event.deltaY * zoomSensitivity;
    
    // Calcular novo zoom
    const newZoom = Math.max(0.25, Math.min(4, zoom + zoomDelta));
    
    // Se o zoom não mudou significativamente, não fazer nada
    if (Math.abs(newZoom - zoom) < 0.001) return;
    
    // Aplicar o zoom
    setZoom(newZoom);
    
    // Log da ação (apenas em dev)
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Zoom:', {
        oldZoom: zoom.toFixed(2),
        newZoom: newZoom.toFixed(2),
        delta: event.deltaY,
        deltaMode: event.deltaMode,
        isTrackpad,
        isMacOS,
        sensitivity: zoomSensitivity
      });
    }
    
    logAction('ZOOM_WHEEL', null, {
      zoom: newZoom,
      delta: event.deltaY,
      deltaMode: event.deltaMode,
      isTrackpad,
      isMacOS
    });
  }, [zoom, logAction]);

  // 🛡️ Prevenir zoom do navegador globalmente (como Google Presentations)
  // Apenas prevenir quando Ctrl/Cmd + Scroll, deixar scroll normal funcionar
  useEffect(() => {
    const handleGlobalWheel = (event) => {
      // 🛡️ CRÍTICO: Se estiver em modo de pinch, sempre prevenir
      if (isPinchingRef.current) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
      
      // Apenas prevenir se for Ctrl/Cmd + Scroll (zoom do navegador ou gesto de pinch no trackpad)
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
      return true;
    };

    // Adicionar listener global com passive: false para poder prevenir
    window.addEventListener('wheel', handleGlobalWheel, { passive: false });
    
    // Prevenir gesture zoom no touch (iOS/Safari)
    const preventGesture = (e) => {
      e.preventDefault();
      return false;
    };
    
    window.addEventListener('gesturestart', preventGesture, { passive: false });
    window.addEventListener('gesturechange', preventGesture, { passive: false });
    window.addEventListener('gestureend', preventGesture, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleGlobalWheel);
      window.removeEventListener('gesturestart', preventGesture);
      window.removeEventListener('gesturechange', preventGesture);
      window.removeEventListener('gestureend', preventGesture);
    };
  }, []);

  const handleMouseUp = useCallback(() => {
    // 🎨 PRIORIDADE MÁXIMA: Se está no modo de pintura, não processar eventos aqui
    if (isSimplePaintingMode) {
      return;
    }

    // 🎯 SEMPRE limpar todos os estados de drag ao soltar mouse
    const wasTransitionDragging = draggingTransitionRef.current.type !== null;
    const wasPointDragging = isDraggingPointRef.current;
    const wasImageEditing = isImageEditingRef.current;
    
    // Limpar controles de transição
    if (wasTransitionDragging) {
      draggingTransitionRef.current = { type: null, angle: null };
      setDraggingTransitionControl(null);
      setDraggingTransitionAngle(null);
      
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.style.cursor = 'default';
      }
    }
    
    // Limpar ponto de controle
    if (wasPointDragging) {
      isDraggingPointRef.current = false;
      setIsDraggingPoint(false);
      // NÃO retornar ainda
    }
    
    // Processar edição de imagem
    if (wasImageEditing) {
      // 📋 LOG: Registrar ação antes de limpar
      const editType = imageEditTypeRef.current;
      const editHandle = imageEditHandleRef.current;
      const imageId = editingImageIdRef.current;
      const startPos = imageEditStartRef.current;
      const imageData = uploadedImages.find(img => img.id === imageId);

      if (imageData) {
        const currentTransform = imageData.transform;
        
        if (editType === 'move') {
          logAction('MOVE_IMAGE', imageId, {
            from: {
              x: currentTransform.x - (lastMousePosRef.current?.x - startPos?.x || 0),
              y: currentTransform.y - (lastMousePosRef.current?.y - startPos?.y || 0)
            },
            to: {
              x: currentTransform.x,
              y: currentTransform.y
            }
          });
        } else if (editType === 'resize') {
          logAction('RESIZE_IMAGE', imageId, {
            handle: editHandle,
            width: currentTransform.width,
            height: currentTransform.height,
            proportional: ['nw', 'ne', 'se', 'sw'].includes(editHandle)
          });
        } else if (editType === 'rotate') {
          logAction('ROTATE_IMAGE', imageId, {
            angle: currentTransform.rotation || 0
          });
        }
      }

      // Cancelar animação pendente
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      
      isImageEditingRef.current = false;
      imageEditStartRef.current = null;
      imageEditTypeRef.current = null;
      imageEditHandleRef.current = null;
      editingImageIdRef.current = null;
      lastMousePosRef.current = null; // Limpar posição do mouse
      lastUpdateTimeRef.current = 0; // Limpar tempo de debounce
      
      // Salvar estado no histórico
      saveHistoryState();
    }
  }, [saveHistoryState, isSimplePaintingMode, uploadedImages, logAction]);

  // 🎬 Efeito para animar o zoom suavemente
  useEffect(() => {
    // Cancelar animação anterior se existir
    if (zoomAnimationRef.current) {
      cancelAnimationFrame(zoomAnimationRef.current);
    }

    const startZoom = animatedZoom;
    const targetZoom = zoom;
    const startTime = performance.now();
    const duration = 200; // 200ms de animação

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Usar easing suave (ease-out)
      const easeOutProgress = 1 - Math.pow(1 - progress, 3);

      const currentZoom = startZoom + (targetZoom - startZoom) * easeOutProgress;
      setAnimatedZoom(currentZoom);

      if (progress < 1) {
        zoomAnimationRef.current = requestAnimationFrame(animate);
      } else {
        zoomAnimationRef.current = null;
      }
    };

    zoomAnimationRef.current = requestAnimationFrame(animate);

    return () => {
      if (zoomAnimationRef.current) {
        cancelAnimationFrame(zoomAnimationRef.current);
      }
    };
  }, [zoom]);

  // Atualizar ref sempre que drawImages mudar
  useEffect(() => {
    drawImagesRef.current = drawImages;
  }, [drawImages]);

  // Efeito para desenhar o canvas
  useEffect(() => {
    if (canvasContext) {
      drawImages(canvasContext);
    }
  }, [canvasContext, drawImages]);

  // 🔧 NOVO: Forçar re-render quando imagens mudam (detecta mudanças no src e lastEdited)
  useEffect(() => {
    if (canvasContext && uploadedImages.length > 0) {
      // Verificar se todas as imagens estão carregadas
      const allImagesLoaded = uploadedImages.every(img => {
        return img.image && img.image.complete && img.image.naturalWidth > 0;
      });

      if (!allImagesLoaded) {
        console.log('⏳ [FluidCanvas] Aguardando carregamento de imagens...');
        const unloadedImages = uploadedImages.filter(img => {
          return !img.image || !img.image.complete || img.image.naturalWidth === 0;
        });
        console.log('  Imagens não carregadas:', unloadedImages.map(img => ({
          id: img.id,
          hasImage: !!img.image,
          complete: img.image?.complete,
          naturalWidth: img.image?.naturalWidth
        })));

        // Re-renderizar após um pequeno delay para dar tempo das imagens carregarem
        const timeout = setTimeout(() => {
          if (canvasContext) {
            console.log('🔄 [FluidCanvas] Re-renderizando após aguardar carregamento...');
            drawImages(canvasContext);
          }
        }, 100);
        return () => clearTimeout(timeout);
      } else {
        // Todas as imagens carregadas, forçar re-render imediato
        console.log('✅ [FluidCanvas] Todas as imagens carregadas, renderizando...');
        drawImages(canvasContext);
      }
    }
  }, [uploadedImages, canvasContext, drawImages]);

  // 🎨 NOVO: Forçar re-render quando sai do modo Pintura2 ou quando imagens são atualizadas
  const lastImagesHashRef = useRef('');
  useEffect(() => {
    if (canvasContext && uploadedImages.length > 0) {
      // Criar hash das imagens baseado em src e lastEdited para detectar mudanças
      const imagesHash = uploadedImages.map(img => 
        `${img.id}:${img.src?.substring(0, 50)}:${img.lastEdited?.getTime() || 0}`
      ).join('|');
      
      // Se o hash mudou, forçar re-renderização
      if (imagesHash !== lastImagesHashRef.current) {
        lastImagesHashRef.current = imagesHash;
        
        // Aguardar um pouco para garantir que as imagens foram carregadas
        const timeout = setTimeout(() => {
          if (canvasContext) {
            console.log('🔄 [FluidCanvas] Re-renderizando devido a mudanças nas imagens');
            drawImages(canvasContext);
          }
        }, 150);
        return () => clearTimeout(timeout);
      }
    }
  }, [uploadedImages, canvasContext, drawImages]);

  // 🎨 NOVO: Forçar re-render quando sai do modo Pintura2
  const wasInPainting2ModeRef = useRef(isPainting2Mode);
  useEffect(() => {
    if (canvasContext && !isPainting2Mode && wasInPainting2ModeRef.current && uploadedImages.length > 0) {
      // Quando sai do modo Pintura2, forçar re-renderização para mostrar atualizações
      console.log('🔄 [FluidCanvas] Saindo do modo Pintura2, forçando re-renderização');
      const timeout = setTimeout(() => {
        if (canvasContext) {
          drawImages(canvasContext);
        }
      }, 200);
      wasInPainting2ModeRef.current = isPainting2Mode;
      return () => clearTimeout(timeout);
    }
    wasInPainting2ModeRef.current = isPainting2Mode;
  }, [isPainting2Mode, canvasContext, drawImages, uploadedImages.length]);

  // Efeito para configurar o canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Configurar canvas de tela cheia com zoom automático
    const handleResize = () => {
      const container = canvas.parentElement?.parentElement; // Pegar o container do wrapper
      if (!container) return;
      
      const containerWidth = container.clientWidth;
      let containerHeight = container.clientHeight;
      
      // Ajustar altura útil no mobile quando o drawer de camadas estiver aberto
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      if (isMobile && mobileLayerDrawerOpen) {
        // Reserva altura proporcional ao drawer (~55vh) e à barra inferior (~48px)
        const reserved = Math.round(window.innerHeight * 0.55) + 48;
        containerHeight = Math.max(120, window.innerHeight - reserved);
      }
      
      // Usar o hook de scaling para configurar o canvas
      const ctx = resizeCanvas(containerWidth, containerHeight);
      if (!ctx) return;
      
      // 📱 No mobile, ajustar escala para fit-to-screen
      // 🖥️ No desktop, usar tamanho real (1:1)
      let autoScale = 1;
      
      // Usar a primeira imagem como referência para dimensões do papel
      const firstImage = uploadedImages[0];
      let paperWidth = 0;
      let paperHeight = 0;
      
      if (firstImage && firstImage.image) {
        paperWidth = firstImage.image.width;
        paperHeight = firstImage.image.height;
      }
      
      if (isMobile && paperWidth > 0 && paperHeight > 0) {
        // Calcular escala para fit com margem de 20px
        const margin = 20;
        const scaleX = (containerWidth - margin * 2) / paperWidth;
        const scaleY = (containerHeight - margin * 2) / paperHeight;
        autoScale = Math.min(scaleX, scaleY, 1); // Nunca aumentar, apenas reduzir
      }
      
      // Se a folha não tem dimensões válidas, usar valores padrão
      let scaledPaperWidth = 0;
      let scaledPaperHeight = 0;
      let paperX = containerWidth / 2;
      let paperY = containerHeight / 2;
      
      if (paperWidth > 0 && paperHeight > 0) {
        // Aplicar escala calculada
        scaledPaperWidth = paperWidth * autoScale;
        scaledPaperHeight = paperHeight * autoScale;
        
        // Calcular posição central da folha no canvas
        // No desktop, considerar 320px da sidebar à esquerda para centralização
        const isDesktop = !isMobile;
        const sidebarWidth = 320; // Largura da sidebar
        
        if (isDesktop) {
          // Área disponível após a sidebar
          const availableWidth = containerWidth - sidebarWidth;
          // Centralizar na área disponível, começando após a sidebar
          paperX = sidebarWidth + (availableWidth - scaledPaperWidth) / 2;
        } else {
          // No mobile, centralizar normalmente
          paperX = (containerWidth - scaledPaperWidth) / 2;
        }
        
        // Centralização vertical permanece igual
        paperY = (containerHeight - scaledPaperHeight) / 2;
      }
      
      setPaperPosition({ 
        x: paperX, 
        y: paperY,
        scale: autoScale,
        width: scaledPaperWidth,
        height: scaledPaperHeight
      });
      
      // Salvar escala para uso posterior
      setCanvasScale(autoScale);
      
      // Redesenhar usando a ref (não causa loop)
      if (drawImagesRef.current) {
        requestAnimationFrame(() => {
          if (drawImagesRef.current) {
            drawImagesRef.current(ctx);
          }
        });
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      // Cancelar animações pendentes ao desmontar
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (zoomAnimationRef.current) {
        cancelAnimationFrame(zoomAnimationRef.current);
      }
    };
  }, [uploadedImages, resizeCanvas, mobileLayerDrawerOpen]);

  // 🎯 Adicionar listener global de mouseup para garantir que sempre capture
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (draggingTransitionRef.current.type) {
        draggingTransitionRef.current = { type: null, angle: null };
        setDraggingTransitionControl(null);
        setDraggingTransitionAngle(null);

        // Resetar cursor
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.style.cursor = 'default';
        }

        // Garantir que não está editando imagem
        isImageEditingRef.current = false;
      }

      // Limpar estado de arrasto do ponto de controle também
      if (isDraggingPointRef.current) {
        isDraggingPointRef.current = false;
        setIsDraggingPoint(false);
      }
    };
    
    document.addEventListener('mouseup', handleGlobalMouseUp);
    
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, []);

  // 📱 Função auxiliar para calcular distância entre dois toques
  const getTouchDistance = (touch1, touch2) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // 📱 Handler para início do toque (pinch-to-zoom e pan)
  const handleTouchStart = useCallback((event) => {
    if (event.touches.length === 2) {
      // Dois dedos = pinch zoom
      isPinchingRef.current = true; // 🛡️ Marcar que está em modo de pinch
      event.preventDefault();
      event.stopPropagation();
      const distance = getTouchDistance(event.touches[0], event.touches[1]);
      touchStartDistanceRef.current = distance;
      touchStartZoomRef.current = zoom;
      lastTouchesRef.current = Array.from(event.touches);
      // Limpar pan quando começar zoom
      touchPanStartRef.current = null;
    } else if (event.touches.length === 1) {
      // Um dedo = pan (apenas no mobile)
      if (window.innerWidth < 768) {
        const touch = event.touches[0];
        touchPanStartRef.current = {
          x: touch.clientX,
          y: touch.clientY
        };
        paperPositionStartRef.current = {
          x: paperPosition.x,
          y: paperPosition.y
        };
        lastTouchesRef.current = Array.from(event.touches);
      }
    }
  }, [zoom, paperPosition.x, paperPosition.y]);

  // 📱 Handler para movimento do toque (pinch-to-zoom e pan)
  const handleTouchMove = useCallback((event) => {
    if (event.touches.length === 2 && touchStartDistanceRef.current) {
      // Pinch zoom (2 dedos)
      isPinchingRef.current = true; // 🛡️ Garantir que a flag está ativa
      event.preventDefault();
      event.stopPropagation(); // 🛡️ CRÍTICO: Bloquear propagação para prevenir zoom do navegador
      const distance = getTouchDistance(event.touches[0], event.touches[1]);
      const scale = distance / touchStartDistanceRef.current;
      const newZoom = Math.max(0.5, Math.min(3, touchStartZoomRef.current * scale));
      setZoom(newZoom);
      lastTouchesRef.current = Array.from(event.touches);
      return false; // 🛡️ Retornar false para prevenir comportamento padrão
    } else if (event.touches.length === 1 && touchPanStartRef.current && paperPositionStartRef.current) {
      // Pan (1 dedo) - arrastar o canvas
      if (window.innerWidth < 768) {
        event.preventDefault();
        const touch = event.touches[0];
        const deltaX = touch.clientX - touchPanStartRef.current.x;
        const deltaY = touch.clientY - touchPanStartRef.current.y;
        
        // Aplicar o movimento à posição do papel
        const newX = paperPositionStartRef.current.x + deltaX / animatedZoom;
        const newY = paperPositionStartRef.current.y + deltaY / animatedZoom;
        
        setPaperPosition(prev => ({
          ...prev,
          x: newX,
          y: newY
        }));
        
        lastTouchesRef.current = Array.from(event.touches);
      }
    }
  }, [animatedZoom]);

  // 📱 Handler para fim do toque
  const handleTouchEnd = useCallback((event) => {
    if (event.touches.length < 2) {
      isPinchingRef.current = false; // 🛡️ Limpar flag de pinch quando soltar um dos dedos
      touchStartDistanceRef.current = null;
      touchStartZoomRef.current = null;
    }
    if (event.touches.length === 0) {
      // Limpar pan quando soltar todos os dedos
      isPinchingRef.current = false; // 🛡️ Garantir que a flag está desativada
      touchPanStartRef.current = null;
      paperPositionStartRef.current = null;
    }
    lastTouchesRef.current = Array.from(event.touches);
  }, []);

  return (
    <div 
      className="absolute inset-0 w-full h-full relative flex items-center justify-center" 
      style={{ 
        backgroundColor: '#252722'
      }}
    >
      {/* Canvas principal: Conteúdo (imagens, efeitos) */}
      <canvas
        ref={canvasRef}
        className="shadow-lg"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onWheel={handleWheel}
        style={{ touchAction: 'none', willChange: 'transform' }}
      />

      {/* Canvas de overlay: Controles visuais (handles, círculos) - Oculto no Mobile */}
      {canvasRef.current && (
        <div className="hidden md:block">
          <ControlsOverlayCanvas
            width={canvasRef.current.width / (window.devicePixelRatio || 1)}
            height={canvasRef.current.height / (window.devicePixelRatio || 1)}
            isEffectsPanelOpen={isEffectsPanelOpen}
            effectsEditingImage={effectsEditingImage}
            activeEffectType={activeEffectType}
            effectSettings={effectSettings}
            effectActiveTab={effectActiveTab}
            paperPosition={paperPosition}
            hoveredTransitionControl={hoveredTransitionControl}
            selectedImageId={selectedImageId}
            zoom={animatedZoom}
          />
        </div>
      )}

      {/* Controles de Zoom - Posicionado na parte inferior central (Oculto no Mobile) */}
      {uploadedImages.length > 0 && (
        <div 
          className="hidden md:block absolute bottom-6 z-30"
          style={{
            left: 'calc(320px + (100% - 320px) / 2)',
            transform: 'translateX(-50%)'
          }}
        >
          <ZoomControls
            zoom={zoom}
            onZoomChange={setZoom}
          />
        </div>
      )}

      {/* Botão de Upload */}
      {uploadedImages.length === 0 ? (
        <>
          {/* Desktop: centralizado considerando sidebar */}
          <div 
            className="hidden md:flex absolute inset-0 items-center justify-center pointer-events-none"
            style={{
              left: '320px'
            }}
          >
            <button
              onClick={() => fileInputRef.current?.click()}
              className="pointer-events-auto flex flex-col items-center gap-3 px-8 py-6 transition-all hover:scale-105"
            >
              <svg className="w-16 h-16" style={{ color: '#7eb715' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-lg font-medium" style={{ color: '#7eb715' }}>
                {t('topBar.uploadImage')}
              </span>
            </button>
          </div>
          {/* Mobile: centralizado normalmente */}
          <div 
            className="md:hidden absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <button
              onClick={() => fileInputRef.current?.click()}
              className="pointer-events-auto flex flex-col items-center gap-3 px-8 py-6 transition-all hover:scale-105"
            >
              <svg className="w-16 h-16" style={{ color: '#7eb715' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-lg font-medium" style={{ color: '#7eb715' }}>
                {t('topBar.uploadImage')}
              </span>
            </button>
          </div>
        </>
      ) : (
        /* Quando há imagens: botões sobre o LayerList - Ocultos no Mobile */
        <div className="hidden md:flex fixed left-6 bottom-6 z-30 items-center gap-2 animate-fadeIn">
          {/* Botão Upload */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-3 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200 transition-all hover:shadow-xl hover:scale-105"
            title="Adicionar mais imagens"
          >
            <svg className="w-5 h-5" style={{ color: '#7eb715' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </button>
          
          {/* Botão Desfazer */}
          <button
            onClick={undo}
            disabled={!canUndo}
            className="p-3 transition-all hover:scale-105 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Desfazer (Ctrl+Z)"
          >
            <svg 
              className="w-5 h-5" 
              style={{ color: canUndo ? '#7eb715' : '#9ca3af' }} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
            </svg>
          </button>
          
          {/* Botão Refazer */}
          <button
            onClick={redo}
            disabled={!canRedo}
            className="p-3 transition-all hover:scale-105 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Refazer (Ctrl+Y)"
          >
            <svg 
              className="w-5 h-5" 
              style={{ color: canRedo ? '#7eb715' : '#9ca3af' }} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" />
            </svg>
          </button>
        </div>
      )}
      
      {/* Galeria de uploads recentes (quando não há imagens) */}
      {uploadedImages.length === 0 && recentUploads.length > 0 && (
        <>
          {/* Desktop: centralizado considerando sidebar */}
          <div 
            className="hidden md:flex absolute bottom-6 flex-col items-center gap-3 z-20 pointer-events-none"
            style={{
              left: 'calc(320px + (100% - 320px) / 2)',
              transform: 'translateX(-50%)'
            }}
          >
            <div className="text-xs text-gray-400">Recentes</div>
            <div className="pointer-events-auto inline-flex flex-wrap justify-center gap-2 bg-transparent p-0 rounded-none shadow-none">
              {recentUploads.map((it) => (
                <button key={it.ts + it.dataUrl} onClick={() => loadFromRecent(it)} className="w-24 h-24 rounded overflow-hidden border border-gray-200 hover:shadow transition">
                  <img src={it.dataUrl} alt={it.name || ''} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
          {/* Mobile: centralizado normalmente */}
          <div className="md:hidden absolute inset-x-0 bottom-6 flex flex-col items-center gap-3 z-20 pointer-events-none">
            <div className="text-xs text-gray-400">Recentes</div>
            <div className="pointer-events-auto inline-flex flex-wrap justify-center gap-2 bg-transparent p-0 rounded-none shadow-none">
              {recentUploads.map((it) => (
                <button key={it.ts + it.dataUrl} onClick={() => loadFromRecent(it)} className="w-24 h-24 rounded overflow-hidden border border-gray-200 hover:shadow transition">
                  <img src={it.dataUrl} alt={it.name || ''} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Input de arquivo oculto */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleImageUpload}
        className="hidden"
      />
      
      {/* Floating Toolbar - só mostra quando não há imagem selecionada */}
      {!selectedImageId && (
        <FloatingToolbar
          isVisible={toolbarVisible}
          position={toolbarPosition}
          direction={toolbarDirection}
          onToolClick={handleToolClick}
        />
      )}

      {/* 🎨 Pintura agora é feita no modal - não precisa de overlay aqui */}
      
    </div>
  );
};

export default FluidCanvas;